import { useEffect, useState } from "react";
import api, { apiError } from "../../../lib/api";
import { toast } from "sonner";
import { useConfirm } from "../../../components/useConfirm";

export function InviteMembers() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [invites, setInvites] = useState([]);
  const [busy, setBusy] = useState(false);
  const { confirm, dialog } = useConfirm();

  const load = () => api.get("/admin/invites").then((r) => setInvites(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/invites", { email, name });
      toast.success(`Invitation sent to ${email}.`);
      setEmail(""); setName("");
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const copyLink = async (inv) => {
    try { await navigator.clipboard.writeText(inv.accept_url); toast.success("Invite link copied."); }
    catch { toast.error("Could not copy the link."); }
  };
  const resend = async (inv) => {
    try { await api.post(`/admin/invites/${inv.invite_id}/resend`); toast.success(`Invitation resent to ${inv.email}.`); load(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const revoke = async (inv) => {
    if (!(await confirm({ title: "Revoke invitation", message: `Revoke the invitation to ${inv.email}? The link will stop working immediately.`, confirmLabel: "Revoke invitation", danger: true }))) return;
    try { await api.post(`/admin/invites/${inv.invite_id}/revoke`); toast.success("Invitation revoked."); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const STATUS = { pending: "Pending", accepted: "Joined", revoked: "Revoked" };

  return (
    <div data-testid="invite-members">
      <span className="t-label">Invite a member</span>
      <p className="t-small text-muted-s mt-2 measure">Send a private invitation by email. The recipient completes their own registration and sets a password. Invitations expire in 14 days.</p>
      <form onSubmit={send} className="mt-5 flex flex-col sm:flex-row gap-3 max-w-3xl">
        <input className="input-solera" type="email" placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="invite-email-input" />
        <input className="input-solera" placeholder="Name (optional)" value={name} onChange={(e) => setName(e.target.value)} data-testid="invite-name-input" />
        <button className="btn-primary shrink-0" disabled={busy || !email} data-testid="send-invite-button">{busy ? "Sending" : "Send invitation"}</button>
      </form>

      {invites.length > 0 && (
        <div className="mt-8 overflow-x-auto">
          <table className="table-solera" data-testid="invites-table">
            <thead><tr><th>Email</th><th>Name</th><th>Status</th><th>Sent</th><th></th></tr></thead>
            <tbody>
              {invites.map((inv) => (
                <tr key={inv.invite_id} data-testid={`invite-row-${inv.invite_id}`}>
                  <td className="text-bone">{inv.email}</td>
                  <td className="text-muted-s">{inv.name || ""}</td>
                  <td className="text-muted-s">{inv.expired && inv.status === "pending" ? "Expired" : STATUS[inv.status] || inv.status}</td>
                  <td className="text-muted-s t-small fig">{inv.created_at?.slice(0, 10)}</td>
                  <td className="text-right whitespace-nowrap space-x-4">
                    {inv.status === "pending" && (
                      <>
                        <button onClick={() => copyLink(inv)} className="t-label hover:!text-[#E8E6E1]" data-testid={`copy-invite-${inv.invite_id}`}>Copy link</button>
                        <button onClick={() => resend(inv)} className="t-label hover:!text-[#E8E6E1]" data-testid={`resend-invite-${inv.invite_id}`}>Resend</button>
                        <button onClick={() => revoke(inv)} className="t-label hover:!text-[#E8E6E1]" data-testid={`revoke-invite-${inv.invite_id}`}>Revoke</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {dialog}
    </div>
  );
}
